/**
 * Orden de las personas dentro de cada fila, y posiciones horizontales de
 * partida.
 *
 * El problema es el clasico de un grafo por capas (Sugiyama): la capa de cada
 * nodo ya esta decidida -- la fila es su generacion y no se negocia -- y lo
 * que queda es ordenar cada capa para que se cruce el menor numero de lineas
 * posible.
 *
 * Tres decisiones que importan:
 *
 *  1. **Se ordenan bloques, no personas.** Las dos personas de una pareja van
 *     pegadas, y alguien con dos parejas forma un bloque de tres con esa
 *     persona en medio. Asi se dibuja un arbol genealogico de verdad y asi se
 *     ven los medios hermanos. Los hermanos quedan contiguos solos, porque
 *     comparten progenitores y por tanto baricentro.
 *
 *  2. **Los cruces se cuentan sobre lo que de verdad se dibuja.** No se
 *     dibuja una linea de cada progenitor a cada hijo, sino una del nudo de
 *     la familia a cada hijo. Contar padre->hijo inflaba la cifra con cruces
 *     que no existen: los de dos lineas de la MISMA familia, que en el dibujo
 *     salen del mismo punto.
 *
 *  3. **Baricentro y luego intercambio.** El baricentro acerca mucho en pocas
 *     pasadas pero se atasca; un barrido final probando intercambios de
 *     bloques vecinos remata lo que queda. Se guarda siempre la mejor
 *     ordenacion vista, no la ultima, porque el baricentro no mejora de forma
 *     monotona.
 */
import type { Indice } from "../core/grafo.js";
import { separacionMinima, type Vecindad } from "./medidas.js";

/** Un bloque: personas de una fila que tienen que ir pegadas. */
export interface Bloque {
  miembros: string[];
  fila: number;
}

/** Una linea del dibujo: del nudo de una familia a un hijo. */
export interface AristaDibujo {
  /** Progenitores presentes: su media da la posicion del nudo. */
  padres: string[];
  hijo: string;
}

export interface Ordenacion {
  /** Fila -> bloques, en orden de izquierda a derecha. */
  filas: Bloque[][];
  /** Persona -> su indice dentro de su fila. */
  indice: Map<string, number>;
  /** Cruces de lineas que quedan, contados sobre lo que se dibuja. */
  cruces: number;
}

// ---------------------------------------------------------------------------
// Bloques
// ---------------------------------------------------------------------------

function construirBloques(
  ix: Indice,
  fila: Map<string, number>,
  personas: string[],
): Bloque[][] {
  const vecinos = new Map<string, Set<string>>();
  for (const h of personas) vecinos.set(h, new Set());
  for (const h of personas) {
    for (const v of ix.parejas(h)) {
      // Solo se pegan si estan en la misma fila. Si no lo estan es que la
      // union se rechazo por crear un ciclo, y ahi manda la estructura.
      if (fila.get(v.otra) !== fila.get(h)) continue;
      vecinos.get(h)?.add(v.otra);
      vecinos.get(v.otra)?.add(h);
    }
  }

  const visto = new Set<string>();
  const porFila = new Map<number, Bloque[]>();

  for (const h of personas) {
    if (visto.has(h)) continue;
    const comp: string[] = [];
    const pila = [h];
    visto.add(h);
    while (pila.length) {
      const x = pila.pop() as string;
      comp.push(x);
      for (const y of vecinos.get(x) ?? []) {
        if (!visto.has(y)) {
          visto.add(y);
          pila.push(y);
        }
      }
    }

    const miembros = comp.length <= 2 ? comp : ordenarComoCamino(comp, vecinos);
    const f = fila.get(h) ?? 0;
    const ls = porFila.get(f);
    if (ls) ls.push({ miembros, fila: f });
    else porFila.set(f, [{ miembros, fila: f }]);
  }

  const max = Math.max(0, ...[...porFila.keys()]);
  const salida: Bloque[][] = [];
  for (let f = 0; f <= max; f++) salida.push(porFila.get(f) ?? []);
  return salida;
}

/**
 * Ordena una componente de parejas como un camino, empezando por un extremo,
 * para que quien tiene dos parejas quede en medio de las dos. Si no es un
 * camino (alguien con tres parejas, o un ciclo) se recorre en anchura, que al
 * menos deja juntas a las que estan unidas.
 */
function ordenarComoCamino(comp: string[], vecinos: Map<string, Set<string>>): string[] {
  const dentro = new Set(comp);
  const grado = (x: string): number =>
    [...(vecinos.get(x) ?? [])].filter((y) => dentro.has(y)).length;
  const extremo = comp.find((x) => grado(x) === 1) ?? comp[0];
  const out: string[] = [];
  const visto = new Set<string>();
  const cola = extremo ? [extremo] : [];
  while (cola.length) {
    const x = cola.shift() as string;
    if (visto.has(x)) continue;
    visto.add(x);
    out.push(x);
    for (const y of vecinos.get(x) ?? []) {
      if (dentro.has(y) && !visto.has(y)) cola.push(y);
    }
  }
  for (const x of comp) if (!visto.has(x)) out.push(x);
  return out;
}

function indicesDe(filas: Bloque[][]): Map<string, number> {
  const idx = new Map<string, number>();
  for (const bloques of filas) {
    let i = 0;
    for (const b of bloques) for (const m of b.miembros) idx.set(m, i++);
  }
  return idx;
}

// ---------------------------------------------------------------------------
// Cruces
// ---------------------------------------------------------------------------

/**
 * Las lineas del dibujo, agrupadas por el hueco entre filas al que pertenecen.
 * Una por (familia, hijo), no una por (progenitor, hijo).
 */
export function aristasDeDibujo(
  ix: Indice,
  fila: Map<string, number>,
): Map<number, AristaDibujo[]> {
  const out = new Map<number, AristaDibujo[]>();
  for (const f of ix.arbol.familias.values()) {
    const padres = [f.padre, f.madre].filter(
      (p): p is string => !!p && ix.arbol.personas.has(p) && fila.has(p),
    );
    if (!padres.length) continue;
    const filaPadres = Math.min(...padres.map((p) => fila.get(p) as number));
    for (const r of f.hijos) {
      const h = r.hlink;
      const fh = fila.get(h);
      if (fh === undefined || fh <= filaPadres) continue;
      const ls = out.get(filaPadres);
      if (ls) ls.push({ padres, hijo: h });
      else out.set(filaPadres, [{ padres, hijo: h }]);
    }
  }
  return out;
}

function posNudo(a: AristaDibujo, idx: Map<string, number>): number {
  let s = 0;
  let c = 0;
  for (const p of a.padres) {
    const v = idx.get(p);
    if (v !== undefined) {
      s += v;
      c++;
    }
  }
  return c ? s / c : 0;
}

/**
 * Cruces entre las lineas de un mismo hueco entre filas.
 *
 * Dos lineas se cruzan si el orden de sus extremos de arriba es el contrario
 * al de los de abajo. Dos lineas de la misma familia salen del mismo nudo, asi
 * que nunca se cruzan entre si.
 *
 * Se cuenta como un numero de inversiones: se ordenan las lineas por el
 * extremo de arriba (y, a igualdad, por el de abajo) y se cuentan los pares
 * que quedan desordenados por abajo, con un arbol de Fenwick. Sale O(E log E)
 * en vez de O(E^2), que es lo que importa cuando el arbol crece: con 2000
 * personas la version cuadratica se comia varios segundos, y esta funcion se
 * llama decenas de veces durante la minimizacion.
 *
 * El empate en el extremo de arriba se resuelve ordenando tambien por abajo,
 * y asi las lineas que salen del mismo nudo no cuentan como cruce, que es
 * justo lo que se quiere.
 */
export function contarCruces(aristas: AristaDibujo[], idx: Map<string, number>): number {
  const n = aristas.length;
  if (n < 2) return 0;

  const pares: Array<[number, number]> = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = aristas[i] as AristaDibujo;
    pares[i] = [posNudo(a, idx), idx.get(a.hijo) ?? 0];
  }
  pares.sort((p, q) => p[0] - q[0] || p[1] - q[1]);

  // Se comprimen las posiciones de abajo a rangos 1..k para el Fenwick.
  const abajo = pares.map((p) => p[1]);
  const distintos = [...new Set(abajo)].sort((a, b) => a - b);
  const rango = new Map<number, number>();
  distintos.forEach((v, i) => rango.set(v, i + 1));

  const k = distintos.length;
  const arbol = new Int32Array(k + 1);
  const sumar = (i: number): void => {
    for (let x = i; x <= k; x += x & -x) arbol[x] = (arbol[x] as number) + 1;
  };
  const suma = (i: number): number => {
    let s = 0;
    for (let x = i; x > 0; x -= x & -x) s += arbol[x] as number;
    return s;
  };

  // Recorriendo de derecha a izquierda, cada linea cruza con las ya vistas
  // que tengan el extremo de abajo estrictamente menor.
  let cruces = 0;
  for (let i = n - 1; i >= 0; i--) {
    const r = rango.get(abajo[i] as number) as number;
    cruces += suma(r - 1);
    sumar(r);
  }
  return cruces;
}

function crucesTotales(
  filas: Bloque[][],
  aristas: Map<number, AristaDibujo[]>,
): number {
  const idx = indicesDe(filas);
  let n = 0;
  for (const [, ls] of aristas) n += contarCruces(ls, idx);
  return n;
}

// ---------------------------------------------------------------------------
// Ordenacion
// ---------------------------------------------------------------------------

/**
 * Ordena las filas minimizando cruces.
 *
 * `pasadas` son las del baricentro; despues viene el refinamiento por
 * intercambio, que es el que remata.
 */
export function ordenar(ix: Indice, fila: Map<string, number>, pasadas = 32): Ordenacion {
  const personas = ix.todas().filter((h) => fila.has(h));
  const filas = construirBloques(ix, fila, personas);
  const aristas = aristasDeDibujo(ix, fila);

  let mejor = filas.map((bs) => [...bs]);
  let mejorCruces = crucesTotales(mejor, aristas);

  /**
   * Reordena una fila por el baricentro de los vecinos.
   *
   * Los bloques sin vecinos en la fila de referencia se quedan en su hueco:
   * moverlos no mejora nada y desordena el resto.
   */
  const pasada = (trabajo: Bloque[][], f: number, haciaAbajo: boolean): void => {
    const bloques = trabajo[f];
    if (!bloques || bloques.length < 2) return;
    // Se recalculan los indices en cada fila, no una vez por barrido: si se
    // usan los de antes, la fila de referencia ya se movio y el baricentro
    // sale de posiciones viejas.
    const idx = indicesDe(trabajo);

    const bariPersona = (h: string): number | undefined => {
      const refs = haciaAbajo ? ix.progenitoresPlanos(h) : ix.hijosDe(h);
      const vs = refs.map((r) => idx.get(r)).filter((v): v is number => v !== undefined);
      if (!vs.length) return undefined;
      return vs.reduce((a, b) => a + b, 0) / vs.length;
    };

    const conPeso = bloques.map((b, i) => {
      const vs = b.miembros.map(bariPersona).filter((v): v is number => v !== undefined);
      return {
        b,
        orden: i,
        peso: vs.length ? vs.reduce((a, x) => a + x, 0) / vs.length : Number.NaN,
      };
    });

    const movibles = conPeso.filter((x) => !Number.isNaN(x.peso));
    movibles.sort((a, b) => a.peso - b.peso || a.orden - b.orden);
    const huecos = conPeso
      .map((x, i) => ({ x, i }))
      .filter(({ x }) => !Number.isNaN(x.peso))
      .map(({ i }) => i);

    const nuevo = conPeso.map((x) => x.b);
    movibles.forEach((m, k) => {
      const destino = huecos[k];
      if (destino !== undefined) nuevo[destino] = m.b;
    });
    trabajo[f] = nuevo;
  };

  const trabajo = filas.map((bs) => [...bs]);
  for (let p = 0; p < pasadas && mejorCruces > 0; p++) {
    const haciaAbajo = p % 2 === 0;
    if (haciaAbajo) {
      for (let f = 1; f < trabajo.length; f++) pasada(trabajo, f, true);
    } else {
      for (let f = trabajo.length - 2; f >= 0; f--) pasada(trabajo, f, false);
    }
    const c = crucesTotales(trabajo, aristas);
    if (c < mejorCruces) {
      mejorCruces = c;
      mejor = trabajo.map((bs) => [...bs]);
    }
  }

  // --- refinamiento --------------------------------------------------
  // El baricentro deja cruces que se quitan a mano. Dos movimientos:
  //
  //   intercambio -- cambiar de sitio dos bloques vecinos
  //   reinsercion -- sacar un bloque y probarlo en todas las posiciones
  //
  // La reinsercion es mucho mas potente (saca de minimos locales donde el
  // intercambio ya no encuentra nada) pero cuesta O(bloques^2) conteos por
  // fila, asi que solo se usa si la fila es manejable. En arboles muy grandes
  // se queda el intercambio, que es lineal.
  const actual = mejor.map((bs) => [...bs]);
  const TOPE_REINSERCION = 120;

  for (let ronda = 0; ronda < 30 && mejorCruces > 0; ronda++) {
    let mejoro = false;

    for (let f = 0; f < actual.length; f++) {
      const bloques = actual[f];
      if (!bloques || bloques.length < 2) continue;

      for (let i = 0; i + 1 < bloques.length; i++) {
        const a = bloques[i] as Bloque;
        const b = bloques[i + 1] as Bloque;
        bloques[i] = b;
        bloques[i + 1] = a;
        const c = crucesTotales(actual, aristas);
        if (c < mejorCruces) {
          mejorCruces = c;
          mejoro = true;
        } else {
          bloques[i] = a;
          bloques[i + 1] = b;
        }
      }

      if (bloques.length > TOPE_REINSERCION) continue;
      for (let i = 0; i < bloques.length; i++) {
        const b = bloques[i] as Bloque;
        let mejorDestino = i;
        let mejorLocal = mejorCruces;
        bloques.splice(i, 1);
        for (let j = 0; j <= bloques.length; j++) {
          if (j === i) continue;
          bloques.splice(j, 0, b);
          const c = crucesTotales(actual, aristas);
          if (c < mejorLocal) {
            mejorLocal = c;
            mejorDestino = j;
          }
          bloques.splice(j, 1);
        }
        bloques.splice(mejorDestino, 0, b);
        if (mejorLocal < mejorCruces) {
          mejorCruces = mejorLocal;
          mejoro = true;
        }
      }
    }

    if (!mejoro) break;
  }

  return { filas: actual, indice: indicesDe(actual), cruces: mejorCruces };
}

// ---------------------------------------------------------------------------
// Posiciones de partida
// ---------------------------------------------------------------------------

/**
 * Posiciones horizontales de partida, respetando las separaciones.
 * La fisica trabaja despues sobre estos valores; no los recalcula desde cero.
 */
export function xIniciales(ord: Ordenacion, ix: Indice): Map<string, number> {
  const x = new Map<string, number>();
  const seps = separacionesPorFila(ord, ix);

  ord.filas.forEach((bloques, f) => {
    const fila = bloques.flatMap((b) => b.miembros);
    const sep = seps[f] ?? [];
    // `cursor` es el centro del nodo que toca colocar.
    let cursor = 0;
    fila.forEach((h, i) => {
      if (i > 0) cursor += sep[i - 1] as number;
      x.set(h, cursor);
    });
  });

  // Cada fila se centra en cero, para que el conjunto quede centrado y el
  // encuadre inicial no tenga que calcular nada raro.
  for (const bloques of ord.filas) {
    const todos = bloques.flatMap((b) => b.miembros);
    if (!todos.length) continue;
    const vs = todos.map((m) => x.get(m) ?? 0);
    const centro = (Math.min(...vs) + Math.max(...vs)) / 2;
    for (const m of todos) x.set(m, (x.get(m) ?? 0) - centro);
  }

  return x;
}

/**
 * Que son entre si dos personas contiguas de una fila.
 *
 * De esto depende cuanto se separan, y de ahi que el diagrama se lea: una
 * pareja casi tocandose, unos hermanos juntos pero despegados, y un hueco
 * ancho entre dos familias distintas.
 */
export function vecindadEntre(ix: Indice, a: string, b: string): Vecindad {
  if (ix.parejas(a).some((v) => v.otra === b)) return "pareja";
  if (ix.claseHermandad(a, b)) return "hermanos";
  return "ajenos";
}

/**
 * La separacion minima entre cada par contiguo de cada fila, en el orden que
 * decidio esta misma pasada.
 *
 * Se calcula una vez y la usan tanto las posiciones de partida como la
 * fisica, para que las dos trabajen con la misma idea de "cerca" y "lejos".
 */
export function separacionesPorFila(ord: Ordenacion, ix: Indice): number[][] {
  return ord.filas.map((bloques) => {
    const fila = bloques.flatMap((b) => b.miembros);
    const out: number[] = [];
    for (let i = 1; i < fila.length; i++) {
      out.push(separacionMinima(vecindadEntre(ix, fila[i - 1] as string, fila[i] as string)));
    }
    return out;
  });
}
